<template>
  <aside class="cover-page">
    <div class="w-full">
      <div class="flex flex-col gap-2 h-[180px] w-[180px] !ml-auto">
        <AppImgPlaceholder
          :src="planStore.logo"
          class="w-full aspect-square"
        />

        <FileUploadButton
          class="btn-dashed btn-outline btn-secondary"
          :icon="faPlus"
          accept=".png, .jpg, .jpeg, .webp"
          text="Upload Municipality Logo"
          @file-selected="handleLogoUpload"
        />

        <AppButton
          v-if="planStore.logo"
          type="button"
          class="btn-dashed btn-outline btn-danger"
          @click="handleLogoRemove"
        >
          <FontAwesomeIcon :icon="faTrashCan" />
          Remove Logo
        </AppButton>
      </div>
    </div>

    <div class="pt-24 pb-36">
      <h1 class="!font-bold">
        {{ planStore.municipality.name }}
      </h1>

      <h2>Integrated Development Plan</h2>

      <h2 class="!mb-12">
        {{ planDuration }}
      </h2>

      <h2 class="!mb-3">
        <TextInput
          v-model.trim="planStore.subtitle"
          class="input-field border hover:border-gray-500"
        />
      </h2>

      <!--navigation feels better with the navigation on here-->
      <p id="logo">
        Last Published: {{ dateFormatter.formatShortDate(planStore.lastPublishedAt) || 'N/A' }}
      </p>
    </div>

    <AppImgPlaceholderAbsolute
      :src="planStore.coverPageImage"
      class="full-width h-[330px] [&_div]:flex [&_div]:gap-2"
    >
      <template v-if="planStore.coverPageImage">
        <FileUploadButton
          class="btn-dashed btn-outline btn-white"
          accept=".png, .jpg, .jpeg, .webp"
          text="Change Cover Page Image"
          :icon="faArrowsRotate"
          @file-selected="handleCoverPageImageUpload"
        />

        <AppButton
          type="button"
          class="btn btn-dashed btn-outline btn-white btn-danger"
          @click="handleCoverPageImageRemove"
        >
          <FontAwesomeIcon :icon="faTrashCan" />
          Remove Image
        </AppButton>
      </template>

      <FileUploadButton
        v-else
        class="btn-dashed btn-outline btn-secondary"
        accept=".png, .jpg, .jpeg, .webp"
        text="Upload Cover Page Image"
        :icon="faPlus"
        @file-selected="handleCoverPageImageUpload"
      />
    </AppImgPlaceholderAbsolute>

    <div class="flex full-width">
      <div
        v-for="color in ['bg-black', 'bg-green-700', 'bg-orange-700', 'bg-blue-700', 'bg-yellow-400']"
        :key="color"
        :class="color"
        class="h-2 flex-1"
      />
    </div>
  </aside>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { useRoute } from 'vue-router';

import { AppImgPlaceholder, AppImgPlaceholderAbsolute, AppButton } from '@/components/app';
import { FileUploadButton } from '@/components/buttons';
import { TextInput } from '@/components/form-inputs';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowsRotate, faPlus, faTrashCan } from '@fortawesome/pro-solid-svg-icons';

import { planDuration } from '@/const';
import { useDateFormatter, useHttp, useToasts } from '@/composables';
import { usePlanStore } from '@/store';

const dateFormatter = useDateFormatter();
const planStore = usePlanStore();
const route = useRoute();
const toasts = useToasts();

const handleLogoUpload = async (file) => {
  const data = new FormData();

  data.append('image', file);

  const response = await useHttp().post(`/municipalities/${route.params.municipality}/plan/logo`, data);

  if (response.statusCode === HttpStatusCode.Ok) {
    planStore.logo = response.data.logo;
    toasts.success('Logo saved successfully');
  } else if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
    toasts.error('The image exceeds the 10MB size limit. Please upload a smaller image.');
  }
};

const handleCoverPageImageUpload = async (file) => {
  const data = new FormData();

  data.append('image', file);

  const response = await useHttp().post(`/municipalities/${route.params.municipality}/plan/cover-page-image`, data);

  if (response.statusCode === HttpStatusCode.Ok) {
    planStore.coverPageImage = response.data.coverPageImage;
    toasts.success('Cover page image saved successfully');
  } else if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
    toasts.error('The photo exceeds the 10MB size limit. Please upload a smaller image.');
  }
};

const handleLogoRemove = async () => {
  const response = await useHttp().delete(`/municipalities/${route.params.municipality}/plan/logo`);

  if (response.statusCode === HttpStatusCode.Ok) {
    planStore.logo = null;
    toasts.success('Logo removed successfully');
  }
};

const handleCoverPageImageRemove = async () => {
  const response = await useHttp().delete(`/municipalities/${route.params.municipality}/plan/cover-page-image`);

  if (response.statusCode === HttpStatusCode.Ok) {
    planStore.coverPageImage = null;
    toasts.success('Cover page image removed successfully');
  }
};

</script>
